import React, { useState, useEffect } from "react";
import {
  Grid, Paper, Button, Typography, List, ListItem, ListItemText, Divider,
  TextField, IconButton, FormControl, Select, MenuItem
} from "@mui/material";
import { ArrowDropDown, Remove } from "@mui/icons-material";
import { toast } from "react-toastify";
import axiosInstance from "../../../utils/axios/axiosInstance";
import Input from "../../../components/common/input/Input";
import API from "../../../config/apiconfig";
import { useDispatch } from "react-redux";
import { userApiLoader } from "../../../action/loaderActions";
import dayjs from "dayjs";
import axios from "axios";

const CATEGORY_OPTIONS = ["TRANSPORTATION", "TRAVEL", "FOOD", "GENERAL"];

const BudgettingTab = () => {
  const dispatch = useDispatch();

  const [budgets, setBudgets] = useState({ Weekly: [], Monthly: [], Yearly: [] });
  const [selectedBudget, setSelectedBudget] = useState(null);

  const [selectedOption, setSelectedOption] = useState("Monthly");
  const [totalBudget, setTotalBudget] = useState("");
  const [categories, setCategories] = useState([{ category: "", limit: "" }]);

  const fetchBudgets = async () => {
    const endpoints = { Weekly: 1, Monthly: 2, Yearly: 3 };
    const updatedBudgets = {};

    await Promise.all(
      Object.entries(endpoints).map(async ([label, id]) => {
        try {
          const res = await axiosInstance.get(`${API.USER_MANAGEMENT_API_BASE_URL}/finance/v1/finance-tracker/budget/${id}`);
          updatedBudgets[label] = [res.data.data];
        } catch (err) {
          if (err?.response?.status === 404) {
            updatedBudgets[label] = [];
          } else {
            toast.error(`Failed to fetch ${label.toLowerCase()} budget`);
          }
        }
      })
    );

    setBudgets(updatedBudgets);
    checkCategorySpendingAgainstBudget(updatedBudgets);
  };

  const fetchPlaidTransactions = async () => {
    const res = await axios.get("https://plaid-2i1u.onrender.com/api/transactions");
    return res.data;
  };

  const checkCategorySpendingAgainstBudget = async (budgetsData) => {
    try {
      const transactions = await fetchPlaidTransactions();

      const categorySpend = {};
      for (let txn of transactions) {
        if (txn.category && txn.amount) {
          for (let cat of txn.category) {
            categorySpend[cat.toUpperCase()] = (categorySpend[cat.toUpperCase()] || 0) + txn.amount;
          }
        }
      }

      Object.entries(budgetsData).forEach(([_, budgetList]) => {
        if (budgetList.length) {
          const b = budgetList[0];
          b.categories.forEach(({ category, limit }) => {
            const spent = categorySpend[category.toUpperCase()] || 0;
            if (spent > limit) {
              toast.warn(`Category "${category}" exceeded budget by ₹${(spent - limit).toFixed(2)}`);
            }
          });
        }
      });
    } catch (err) {
      toast.error("Failed to fetch Plaid transactions");
    }
  };

  useEffect(() => {
    fetchBudgets();
  }, []);

  const handleBudgetSelect = (budget, label) => {
    setSelectedBudget(budget);
    setSelectedOption(label);
    setTotalBudget(budget.total_budget);
    setCategories(budget.categories || [{ category: "", limit: "" }]);
  };

  const handleAddCategory = () => setCategories([...categories, { category: "", limit: "" }]);
  const handleRemoveCategory = (index) => setCategories(categories.filter((_, i) => i !== index));

  const handleCategoryChange = (index, field, value) => {
    const updated = [...categories];
    updated[index][field] = value;
    setCategories(updated);
  };

  const handleSubmit = async () => {
    dispatch(userApiLoader({ isApiLoader: true }));
    const budgetPeriodMap = { Daily: 1, Weekly: 1, Monthly: 2, Yearly: 3 };
    const today = dayjs();
    const payload = {
      budget_period: budgetPeriodMap[selectedOption],
      total_budget: parseFloat(totalBudget),
      budget_year: today.year(),
      budget_month_year: today.format("YYYY-MM"),
      budget_week_start_date: today.startOf("week").format("YYYY-MM-DD"),
      budget_week_end_date: today.endOf("week").format("YYYY-MM-DD"),
      categories: categories.filter((c) => c.category && c.limit)
    };

    axiosInstance.post(
      `${API.USER_MANAGEMENT_API_BASE_URL}/finance/v1/finance-tracker/budget/create`,
      payload
    ).then(res => {
      dispatch(userApiLoader({ isApiLoader: false }));
      toast.success(res?.data?.message || "Budget saved");
      fetchBudgets();
      setSelectedBudget(null);
    }).catch(err => {
      dispatch(userApiLoader({ isApiLoader: false }));
      toast.error(err?.response?.data?.message || "Failed to save budget");
    });
  };

  return (
    <Grid container spacing={2}>
      {/* Budget List */}
      <Grid item xs={12} lg={4}>
        <Paper elevation={3} className="p-4">
          <Typography variant="h6" className="mb-3">Budgets</Typography>
          {["Weekly", "Monthly", "Yearly"].map(label => (
            <div key={label}>
              <Typography className="font-semibold text-primary">{label}</Typography>
              {budgets[label].length > 0 ? (
                <List dense>
                  {budgets[label].map(b => (
                    <ListItem key={b.id} button onClick={() => handleBudgetSelect(b, label)}>
                      <ListItemText
                        primary={`₹ ${b.total_budget}`}
                        secondary={b.categories.map(c => `${c.category}: ₹${c.limit}`).join(", ")}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography className="text-sm text-gray-500 ml-2">No data found.</Typography>
              )}
              <Divider className="my-3" />
            </div>
          ))}
        </Paper>
      </Grid>

      {/* Budget Form */}
      <Grid item xs={12} lg={8}>
        <Paper elevation={3} className="p-5">
          <Typography variant="h6" className="mb-4">Budget Form</Typography>
          <div className="mb-4">
            <label className="block mb-2">Enter Total Budget</label>
            <div className="flex gap-2">
              <Input type="number" value={totalBudget} onChange={(e) => setTotalBudget(e.target.value)} />
              <FormControl fullWidth>
                <Select
                  value={selectedOption}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  IconComponent={(props) => <ArrowDropDown {...props} />}
                >
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Yearly">Yearly</MenuItem>
                </Select>
              </FormControl>
            </div>
          </div>

          <div className="mb-4">
            <label className="block mb-2">Set Limits by Category</label>
            {categories.map((item, index) => (
              <div key={index} className="flex gap-2 items-center mb-2">
                <TextField
                  label="Limit"
                  type="number"
                  value={item.limit}
                  onChange={(e) => handleCategoryChange(index, "limit", e.target.value)}
                />
                <FormControl fullWidth>
                  <Select
                    value={item.category}
                    onChange={(e) => handleCategoryChange(index, "category", e.target.value)}
                    displayEmpty
                  >
                    <MenuItem disabled value="">
                      <em>Select Category</em>
                    </MenuItem>
                    {CATEGORY_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {categories.length > 1 && (
                  <IconButton onClick={() => handleRemoveCategory(index)}>
                    <Remove color="error" />
                  </IconButton>
                )}
              </div>
            ))}
            <Button onClick={handleAddCategory} variant="outlined" color="primary">+ Add Category</Button>
          </div>

          <Button variant="contained" color="primary" fullWidth onClick={handleSubmit}>
            {selectedBudget ? "Update Budget" : "Create Budget"}
          </Button>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default BudgettingTab;
